/**
 * Environment Detection
 * Determines the runtime environment for adapter selection
 */

export const Environment = {
  ELECTRON: 'electron',
  WEB_PORTAL: 'web-portal',
  DEVELOPMENT: 'development',
}

/**
 * Detect the current runtime environment
 *
 * @returns {string} One of Environment values
 */
export function detectEnvironment() {
  // Check if running in Electron (electronAPI is exposed by preload.js)
  if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
    return Environment.ELECTRON
  }

  // Check if we're in the web portal (production web build)
  if (typeof window !== 'undefined' && import.meta.env.VITE_MODE === 'portal') {
    return Environment.WEB_PORTAL
  }

  // Check for production web deployment
  if (typeof window !== 'undefined' && import.meta.env.PROD && !window.electron) {
    return Environment.WEB_PORTAL
  }

  // Default to development
  return Environment.DEVELOPMENT
}

/**
 * Check if running in Electron
 */
export function isElectron() {
  return detectEnvironment() === Environment.ELECTRON
}

/**
 * Check if running in Web Portal mode
 */
export function isWebPortal() {
  return detectEnvironment() === Environment.WEB_PORTAL
}

/**
 * Check if running in development mode
 */
export function isDevelopment() {
  return detectEnvironment() === Environment.DEVELOPMENT
}

/**
 * Check if running on the QA EC2 instance.
 * Used to gate features that are not ready for QA.
 */
export function isQA() {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'recodeit.ai' || host === '100.29.224.182' || host === '98.93.44.193'
}

/**
 * Get the API base URL based on environment
 * Updated: January 22, 2026 - Removed fallback pattern (violates Rule #3)
 */
export function getAPIBaseUrl() {
  const env = detectEnvironment()

  if (env === Environment.ELECTRON) {
    // Electron connects to local API
    return 'http://localhost:8000'
  }

  if (env === Environment.WEB_PORTAL) {
    // Web portal - routed through CloudFront → nginx → uvicorn (no mixed content)
    return 'https://recodeit.ai/api'
  }

  // Development - Local API
  return 'http://localhost:8000'
}
